import { concat } from 'shared/collection';
import { fmtNum } from 'shared/format';
import { max } from 'shared/agg';
import { notNull } from 'shared/type';
import { Fmt } from 'shared/forecast/Fmt';
import { useElementSize } from 'shared/ui';
import { ComponentProps, CSSProperties, RefObject, useContext, useRef, useEffect } from 'react';
import { clamp, flatMap, flat, isNumber, last, pick, sortBy, entries } from 'remeda';
import { Annotation, AnnotationLabel, AnnotationLineSubject, AnnotationCircleSubject, AreaSeries, Axis, Tooltip, XYChart, LineSeries, TooltipContext } from '@visx/xychart';
import { Dist, DistributionsSharedCfg } from './distributions';
import { Text } from '@visx/text';
import { UtcScaleConfig, LinearScaleConfig } from '@visx/scale';
import { scaleLinear, ScaleLinear, scaleUtc, type ScaleTime } from 'd3-scale';
import { Rnge } from 'shared/range';
import { useSafeMantineTheme } from '@mantine/core';
import { P, match } from 'ts-pattern';
import { useHover, useEmSize } from '../ui';
import { useMergedRef } from '@mantine/hooks';
import { DateTime } from 'luxon';
const {
  round
} = Math;
export type DistItem = Dist.DiscreteDistribution & {
  color?: string;
  lineProps?: Partial<Pick<ComponentProps<typeof LineSeries>, 'stroke' | 'strokeDasharray' | 'strokeOpacity'>>;
  tipTitle?: string;
};
export type DistributionChartProps = {
  chart: DistributionsSharedCfg & {
    formats: Fmt.OptionSize;
  };
  dist?: DistItem;
  dists?: DistItem[];
  hideLine?: boolean;
  hideLabels?: boolean;
  yAccessor?: (pt: Dist.Point) => number;
  xScale?: ScaleLinear<number, number, never>;
  yScale?: ScaleLinear<number, number, never>;
  margin?: {
    left: number;
    right: number;
    bottom?: number;
    top?: number;
  };
  fadeLabelsOnHover?: boolean;
  fillParentHeight?: boolean;
  tipOffsetY?: string;
  debugName?: string;
};
export const DistributionChart = ({
  hideLabels,
  hideLine,
  dist: distRaw,
  dists: distsRaw,
  yAccessor = pt => pt?.p,
  chart: {
    bounds: boundsRaw,
    formats,
    axisLabel,
    watermark,
    watermarkProps,
    primaryColor
  },
  debugName,
  fadeLabelsOnHover = true,
  fillParentHeight,
  tipOffsetY,
  ...props
}: DistributionChartProps) => {
  const em = useEmSize();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const {
    ref: dimRef,
    width: containerWidth,
    height: heightRaw
  } = useElementSize();
  const {
    colors,
    fontFamily
  } = useSafeMantineTheme();
  const {
    hovered,
    ref: hoverRef
  } = useHover();
  const m = {
    left: 10,
    right: 10,
    top: 10,
    bottom: hideLabels ? 3 : 20,
    ...props.margin
  };
  const isDateAxis = formats.default.type == 'date';
  const dists = concat(distRaw ? {
    ...distRaw,
    distType: 'main'
  } : undefined, distsRaw?.map(d => ({
    ...d,
    distType: 'list'
  }))).map((d_0, i) => ({
    ...d_0,
    dataKey: `area-${i}`
  }));
  const labelProps = {
    fontSize: '1em',
    fontFamily,
    fill: colors.gray[5],
    opacity: 0.8,
    stroke: '#FFF',
    paintOrder: 'stroke',
    strokeWidth: 3
  };
  const s = {
    labelProps,
    text: {
      ...pick(labelProps, ['fontSize', 'fontFamily'])
    } as CSSProperties,
    metric: {
      b1: {
        ...labelProps,
        fontSize: '18px',
        fill: 'var(--mantine-color-primary-filled)',
        fontWeight: 'bold',
        opacity: 0.9
      } as CSSProperties,
      b2: {
        ...labelProps,
        fontSize: '14px',
        fill: colors.gray[5],
        fontWeight: 'bold',
        opacity: 0.9
      }
    }
  };
  const yScale = props.yScale ?? scaleLinear([0, max(flatMap(dists, d_1 => d_1.pts.map(yAccessor)))], [(fillParentHeight ? heightRaw : 180) - m.top - m.bottom, m.top]);
  const chartHeight = Rnge.width(yScale.range() as [number, number]) + m.top + m.bottom;
  return <div ref={useMergedRef(dimRef, hoverRef, chartContainerRef)} id='chart-container' className='relative w-full select-none' style={{
    height: fillParentHeight ? '100%' : chartHeight
  }}>
      {/* just put a blank div inside to get styling ot calculate text width */}
      <div style={{
      display: 'none'
    }} ref={em.ref} />
      {match(containerWidth).with(0, () => <></>).otherwise(w => {
      const xDomain = props.xScale?.domain() ?? boundsRaw;
      const xRange = props.xScale?.range() ?? [m.left, w - m.right];
      const width = Rnge.width(xRange) + m.left + m.right;
      // visx does its own version of this, we still need it when we are overriding placement of things
      const xScale = isDateAxis ? scaleUtc(xDomain, xRange) : scaleLinear(xDomain, xRange);
      const desiredTicks = match(width).with(P.number.lt(300), () => 3).with(P.number.lt(600), () => 5).otherwise(() => 8);
      const tickFmt = createTickFormatter(isDateAxis, {
        xScale,
        formats,
        desiredTicks
      });
      return <XYChart xScale={isDateAxis ? {
        type: 'utc',
        domain: xDomain,
        range: xRange,
        round: true
      } satisfies UtcScaleConfig : {
        type: 'linear',
        zero: false,
        domain: xDomain,
        range: xRange
      } satisfies LinearScaleConfig} yScale={{
        type: 'linear',
        domain: yScale.domain(),
        range: yScale.range()
      }} height={chartHeight} width={width} margin={m}>
              {dists.map(d_2 => {
          const props_0 = {
            dataKey: d_2.dataKey,
            data: d_2.pts,
            xAccessor: (d_3: Dist.Point) => d_3?.x,
            yAccessor
          };
          const key = d_2.dataKey;
          const color = d_2.color ?? primaryColor;
          return d_2.distType == 'list' ? <LineSeries key={key} {...props_0} {...{
            stroke: color,
            visibility: !hideLine ? 'visible' : 'hidden'
          }} {...d_2.lineProps} /> : <AreaSeries key={key} {...props_0} fill={color} fillOpacity={0.2} lineProps={{
            stroke: color,
            visibility: !hideLine ? 'visible' : 'hidden',
            strokeOpacity: 0.5,
            ...d_2.lineProps
          }} />;
        })}

              {!hideLabels && <Axis orientation='bottom' label={axisLabel} labelProps={{
          ...s.labelProps,
          textAnchor: 'end',
          verticalAnchor: 'end',
          x: Rnge.width(xScale.range()) - m.right,
          fontStyle: 'italic'
        }} labelOffset={-20} hideAxisLine tickLineProps={{
          stroke: colors.gray[3]
        }} tickLabelProps={() => ({
          ...labelProps,
          fontSize: '0.9em',
          fontWeight: '500'
        })} tickFormat={tickFmt} numTicks={desiredTicks} />}

              {flat(dists.map(d_4 => sortBy(d_4.labels ?? [], l => match(l.type).with('mainPoint', () => 3).with('point', () => 2).with('xTop', 'xBottom', () => 0).otherwise(() => 1)).map((pointLabel, i_0) => {
          const {
            x,
            title: titleRaw,
            subTitle,
            color: color_0,
            lineProps,
            type,
            labelProps: labelProps_0
          } = pointLabel;
          const titleProps = {
            opacity: fadeLabelsOnHover && hovered ? 0.1 : 1,
            ...pointLabel.titleProps
          };
          const isLastXTopLabel = last(sortBy(d_4.labels?.filter(l_0 => l_0.type == 'xTop') ?? [], l_1 => l_1.x)) == pointLabel;
          const point = d_4.adjustedPt(x);
          const title = titleRaw ?? Fmt.fmt(x, hideLabels ? formats.default : formats.long);
          const mainLabelWidth = clamp(Math.max(
          // in practice i grabbed the computed size, there are too many layers to do this logically
          em.getTextWidth(title, {
            font: {
              fontSize: s.metric.b1.fontSize?.toString(),
              fontWeight: '700'
            }
          }), em.getTextWidth(subTitle ?? '', {
            font: {
              fontSize: s.metric.b1.fontSize?.toString()
            }
          })) + 8,
          // pad to avoid wrap due to minor differences
          {
            min: 50,
            max: width * 0.8
          });
          let mainLabelX = xScale(x) - 30;
          if (mainLabelX < 0) mainLabelX = 0;
          if (mainLabelX + mainLabelWidth + 20 > width) mainLabelX = width - mainLabelWidth - 20;
          if (!point) return undefined;
          return <Annotation key={i_0} dataKey={d_4.dataKey} datum={point} dx={0} dy={1}>
                        {['xTop', 'xBottom'].includes(type ?? '') && <AnnotationLineSubject {...{
              stroke: color_0 ?? colors.gray[5],
              opacity: 0.5,
              strokeDasharray: '5,5',
              strokeWidth: 3,
              strokeLinecap: 'round'
            }} {...lineProps} />}

                        {!hideLabels && ['xTop', 'xBottom'].includes(type ?? '') && <AnnotationLabel title={titleRaw} subtitle={subTitle} showBackground={false} showAnchorLine={false} backgroundPadding={0} titleProps={{
              ...s.labelProps,
              ...(isLastXTopLabel ? {
                x: -10
              } : {}),
              textAnchor: isLastXTopLabel ? 'start' : 'middle',
              ...titleProps
            }} subtitleProps={{
              dy: 0
            }} fontColor={color_0 ?? primaryColor} y={type == 'xTop' ? 0 : chartHeight - 38} horizontalAnchor={isLastXTopLabel ? 'start' : 'middle'} {...labelProps_0} />}
                        {type == 'mainPoint' && <AnnotationLabel title={title} subtitle={subTitle} showBackground={false} showAnchorLine={false} titleProps={{
              ...s.labelProps,
              fill: color_0 ?? primaryColor ?? s.metric.b1.fill,
              strokeWidth: 4,
              ...titleProps
            }} subtitleProps={{
              ...s.labelProps,
              fill: color_0 ?? primaryColor ?? s.metric.b1.fill,
              ...titleProps
            }} width={mainLabelWidth} y={5} x={mainLabelX} verticalAnchor='start' horizontalAnchor='start' backgroundPadding={0} titleFontSize={s.metric.b1.fontSize} subtitleFontSize={s.metric.b2.fontSize} subtitleFontWeight={'semibold'} {...labelProps_0} />}
                        {type == 'point' && <>
                            {titleRaw && <AnnotationLabel title={titleRaw} showBackground={false} showAnchorLine={false} titleProps={{
                ...s.labelProps,
                fill: color_0 ?? primaryColor ?? s.metric.b1.fill,
                ...titleProps
              }} {...labelProps_0} />}
                            <AnnotationCircleSubject radius={3} />
                          </>}
                      </Annotation>;
        }))).filter(notNull)}
              {watermark && <Text verticalAnchor='end' textAnchor='end' x={width - 45} y={heightRaw - 30} {...watermarkProps ?? {}} style={{
          ...s.metric.b2,
          ...(watermarkProps?.style ?? {})
        }}>
                  {watermark}
                </Text>}

              <ChartTooltip formats={formats} dists={dists} s={s} chartContainerRef={chartContainerRef} tipOffsetY={tipOffsetY} />
            </XYChart>;
    })}
    </div>;
};
export const createTickFormatter = <TIsDate extends boolean,>(isDate: TIsDate, {
  xScale,
  formats,
  desiredTicks
}: {
  xScale: TIsDate extends true ? ScaleTime<number, number> : ScaleLinear<number, number>;
  formats: Fmt.OptionSize;
  desiredTicks?: number | undefined;
}): ((value: TIsDate extends true ? Date : number) => string) => {
  type V = TIsDate extends true ? Date : number;
  type Res = (value: V) => string;
  const checkDuplicates = (formatter: Res) => {
    const seen = new Set<string>();
    const baseTicks = xScale.ticks(desiredTicks);
    return baseTicks.some(tickRaw => {
      const formatted = formatter(tickRaw as V);
      if (seen.has(formatted)) return true;
      seen.add(formatted);
      return false;
    });
  };
  const rawFormatter: Res = (tick: V) => isDate ? dateAxisFmt(DateTime.fromJSDate(tick as Date).toUTC()) : Fmt.fmt(tick as number, formats.short);
  if (!checkDuplicates(rawFormatter)) return rawFormatter;
  return match(isDate as boolean).with(true, () => {
    const xScaleDate = xScale as ScaleTime<number, number>;
    const left = DateTime.fromJSDate(xScaleDate.domain()[0]!);
    const right = DateTime.fromJSDate(xScaleDate.domain()[1]!);
    const fmtString = match(right.diff(left, 'days').days).with(P.number.lt(1), () => 'HH:mm').with(P.number.lt(7), () => 'ha d LLL').with(P.number.lt(30), () => 'd LLL').with(P.number.lt(365), () => 'LLL yyyy').otherwise(() => 'yyyy');
    return (d: Date) => dateAxisFmt(DateTime.fromJSDate(d).toUTC(), fmtString);
  }).otherwise(() => {
    const xScaleNum = xScale as ScaleLinear<number, number>;
    const domain = xScaleNum.domain();
    const domainRange = domain[1]! - domain[0]!;
    let precision = Math.max(0, Math.ceil(-Math.log10(domainRange))) + 1;
    while (precision <= 10) {
      const fmt = `#${precision > 0 ? `[.]${'0'.repeat(precision)}` : ''}a`;
      const testFormatter = (d: number) => Fmt.fmt(d, {
        fmt,
        unit: formats.short.unit
      });
      if (!checkDuplicates(testFormatter as Res)) return testFormatter as Res;
      precision++;
    }
    // fallback to maximum precision
    const fmt = `#[.]${'0'.repeat(10)}a`;
    return ((d: number) => Fmt.fmt(d, {
      fmt,
      unit: formats.short.unit
    })) as Res;
  }) as Res;
};

/** re-implements the d3 style date formatting but with months as 3 letters */
const dateAxisFmt = (d: DateTime, fmt?: string) => {
  if (d.startOf('year').equals(d)) return d.toFormat('yyyy');
  if (d.startOf('month').equals(d)) return d.toFormat('LLL');
  if (d.startOf('day').equals(d)) return d.toFormat('d LLL');
  return d.toFormat(fmt ?? 'd LLL');
};
const ChartTooltip = ({
  formats,
  dists,
  s,
  chartContainerRef,
  tipOffsetY
}: {
  formats: Fmt.OptionSize;
  dists: (DistItem & {
    dataKey: string;
  })[];
  s: {
    text: CSSProperties;
    metric: {
      b1: CSSProperties;
      b2: CSSProperties;
    };
  };
  chartContainerRef: RefObject<HTMLDivElement | null>;
  tipOffsetY?: string;
}) => {
  const {
    colors
  } = useSafeMantineTheme();
  const ctx = (useContext as any)(TooltipContext);

  // Add scroll event listener
  useEffect(() => {
    if (!ctx) return;
    const handleScroll = () => {
      if (ctx.tooltipOpen) ctx.hideTooltip();
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [ctx]);
  const chartPosish = chartContainerRef.current?.getBoundingClientRect();
  return <Tooltip offsetLeft={0} detectBounds style={{
    zIndex: 100,
    position: 'fixed',
    background: 'var(--mantine-color-text, #000)',
    color: 'var(--mantine-color-default, #FFF)',
    padding: '0rem 0.3rem',
    boxShadow: 'rgba(33, 37, 41, 0.333) 0px 2px 4px',
    borderRadius: '0.3rem',
    transform: chartPosish && ctx?.tooltipLeft && ctx?.tooltipTop ? `translate(calc(${ctx.tooltipLeft + chartPosish.left}px - 50%), calc(${chartPosish.bottom}px + ${tipOffsetY ?? '0px'}))` : undefined,
    pointerEvents: 'none'
  }} showVerticalCrosshair verticalCrosshairStyle={{
    stroke: colors.gray[3],
    strokeWidth: 2,
    strokeDasharray: '5,5'
  }} renderTooltip={({
    tooltipData: t
  }) => {
    if (!t?.datumByKey) return undefined;
    const points = entries(t.datumByKey).map(([key, {
      datum
    }]) => ({
      key,
      d: datum as Dist.Point
    }));
    return points.length > 0 && <div id='chart-tip' style={{
      ...s.text,
      padding: '0.3em'
    }}>
              <b style={{
        ...s.metric.b1
      }}> {Fmt.fmt(points[0]!.d.x, formats.long)}</b>
              {points.map(({
        key: key_0,
        d
      }, i) => {
        const dist = dists.find(d_0 => d_0.dataKey == key_0);
        const color = points.length > 1 ? dist?.color : undefined;
        const title = dist?.tipTitle;
        return <div key={key_0}>
                    <div style={{
            fontSize: '0.8em',
            color
          }}>
                      {title && <span>{title}: </span>}
                      <b style={{
              ...s.metric.b2
            }} className=''>
                        {fmtNum(round((d.pAcc ?? 0) * 100), '0#o')}
                      </b>
                      {i == 0 && <span> percentile</span>}
                    </div>
                  </div>;
      })}
            </div>;
  }} />;
};